import {
	confirmPasswordReset,
	EmailAuthProvider,
	FacebookAuthProvider,
	updatePassword as firebaseUpdatePassword,
	GoogleAuthProvider,
	reauthenticateWithCredential,
	signInWithEmailAndPassword,
	signInWithPopup,
	signOut,
	verifyPasswordResetCode,
} from 'firebase/auth';
import type { Auth, UserCredential } from 'firebase/auth';

export type SignInWithEmailArgs = {
	email:    string
	password: string
};

export type ResetPasswordArgs = {
	oobCode:  string
	password: string
};

export type UpdatePasswordArgs = {
	email:       string
	newPassword: string
	password:    string
};

/**
 * Firebase error code to user-friendly message mappings
 * Organized by operation type for better maintainability
 */
const ERROR_MESSAGES = {
	// Common authentication errors
	auth: new Map([
		[ 'auth/invalid-email', 'Invalid email address format' ],
		[ 'auth/too-many-requests', 'Too many failed login attempts. Please try again later' ],
		[ 'auth/user-disabled', 'This account has been disabled' ],
		[ 'auth/user-not-found', 'No account found with this email address' ],
		[ 'auth/wrong-password', 'Incorrect password' ],
	]),

	// Password reset specific errors
	passwordReset: new Map([
		[ 'auth/expired-action-code', 'Password reset link has expired. Please request a new one' ],
		[ 'auth/invalid-action-code', 'Invalid password reset link. Please request a new one' ],
		[ 'auth/user-disabled', 'This account has been disabled' ],
		[ 'auth/user-not-found', 'No account found for this password reset link' ],
		[ 'auth/weak-password', 'Password is too weak. Please choose a stronger password' ],
	]),

	// Password update specific errors
	passwordUpdate: new Map([
		[
			'auth/requires-recent-login',
			'This operation requires recent authentication. Please sign in again',
		],
		[ 'auth/user-mismatch', 'The provided email does not match the current user' ],
		[ 'auth/weak-password', 'New password is too weak. Please choose a stronger password' ],
		[ 'auth/wrong-password', 'Current password is incorrect' ],
	]),

	// Popup/Provider specific errors
	popup: new Map([
		[
			'auth/account-exists-with-different-credential',
			'An account already exists with the same email but different sign-in method',
		],
		[ 'auth/cancelled-popup-request', 'Only one popup request is allowed at a time' ],
		[ 'auth/popup-blocked', 'Popup was blocked by browser. Please allow popups and try again' ],
		[ 'auth/popup-closed-by-user', 'Sign-in popup was closed before completion' ],
	]),
} as const;

/**
 * Helper function to handle Firebase errors with operation-specific messages
 * @param error Firebase error object
 * @param errorMaps Array of error message maps to check
 * @returns never (always throws)
 */
function handleFirebaseError(
	error: { code?: string; message?: string },
	...errorMaps: Map<string, string>[]
): never {
	// Check each provided error map for the error code
	for (const errorMap of errorMaps) {
		const message = error.code ? errorMap.get(error.code) : undefined;

		if (message) {
			throw new Error(message);
		}
	}

	throw new Error(error?.message || 'An unknown error occurred');
}

export class FirebaseAuthHandler {
	constructor(private readonly auth: Auth) {}

	/**
	 * Get current user object
	 * @returns Firebase User object or null
	 */
	getCurrentUser() {
		return this.auth.currentUser;
	}

	/**
	 * Get current user's ID token (for API authentication)
	 * @param forceRefresh Force refresh of the token
	 * @returns ID token string or null if no user
	 */
	async getIdToken(forceRefresh: boolean = false): Promise<null | string> {
		try {
			const user = this.auth.currentUser;

			if (!user) {
				return null;
			}

			return await user.getIdToken(forceRefresh);
		}
		catch (error: unknown) {
			console.error('Error getting ID token:', error);
			throw new Error('Failed to get authentication token');
		}
	}

	/**
	 * Check if user is currently authenticated
	 * @returns boolean indicating auth status
	 */
	isAuthenticated(): boolean {
		return this.auth.currentUser !== null;
	}

	/**
	 * Sign out the current user
	 * Clears Firebase auth state locally
	 */
	async logout(): Promise<void> {
		try {
			await signOut(this.auth);
		}
		catch (error: unknown) {
			console.error('Error during logout:', error);
			throw new Error('Logout failed. Please try again');
		}
	}

	/**
	 * Reset password using password reset code (from email link)
	 * @param oobCode Out-of-band code from password reset email
	 * @param password New password
	 */
	async resetPassword({ oobCode, password }: ResetPasswordArgs): Promise<void> {
		try {
			await verifyPasswordResetCode(this.auth, oobCode);
			await confirmPasswordReset(this.auth, oobCode, password);
		}
		catch (error: unknown) {
			handleFirebaseError(
				error as { code?: string; message?: string },
				ERROR_MESSAGES.passwordReset,
			);
		}
	}

	/**
	 * Sign in user with email and password
	 * @param email User's email
	 * @param password User's password
	 * @returns UserCredential with user info and ID token
	 */
	async signInWithEmail({ email, password }: SignInWithEmailArgs): Promise<UserCredential> {
		console.log('🚀 ~ FirebaseAuthHandler ~ signInWithEmail ~ password:', password);
		console.log('🚀 ~ FirebaseAuthHandler ~ signInWithEmail ~ email:', email);

		try {
			const userCredential = await signInWithEmailAndPassword(this.auth, email, password);

			return userCredential;
		}
		catch (error: unknown) {
			handleFirebaseError(error as { code?: string; message?: string }, ERROR_MESSAGES.auth);
		}
	}

	/**
	 * Sign in with third-party provider (Google, Facebook, etc.)
	 * @param provider Provider name ('google', 'facebook')
	 * @returns UserCredential with user info and ID token
	 */
	async signInWithProvider(provider: string): Promise<UserCredential> {
		try {
			let authProvider;

			switch (provider.toLowerCase()) {
				case 'facebook':
					authProvider = new FacebookAuthProvider();
					authProvider.addScope('email');
					break;
				case 'google':
					authProvider = new GoogleAuthProvider();
					authProvider.addScope('email');
					authProvider.addScope('profile');
					break;
				default:
					throw new Error(`Unsupported provider: ${provider}`);
			}

			const userCredential = await signInWithPopup(this.auth, authProvider);

			return userCredential;
		}
		catch (error: unknown) {
			handleFirebaseError(
				error as { code?: string; message?: string },
				ERROR_MESSAGES.popup,
				ERROR_MESSAGES.auth,
			);
		}
	}

	/**
	 * Update user's password (requires recent authentication)
	 * @param email User's email for reauthenticate
	 * @param password Current password for reauthenticate
	 * @param newPassword New password to set
	 */
	async updatePassword({ email, newPassword, password }: UpdatePasswordArgs): Promise<void> {
		try {
			const user = this.auth.currentUser;

			if (!user) {
				throw new Error('No user is currently signed in');
			}

			// Reauthenticate user before updating password (Firebase security requirement)
			const credential = EmailAuthProvider.credential(email, password);
			await reauthenticateWithCredential(user, credential);
			await firebaseUpdatePassword(user, newPassword);
		}
		catch (error: unknown) {
			handleFirebaseError(
				error as { code?: string; message?: string },
				ERROR_MESSAGES.passwordUpdate,
				ERROR_MESSAGES.auth,
			);
		}
	}
}
